import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import CustomSelect from "../../components/Ui/CustomSelect";

const PAGE_SIZE = 10;

export default function MySubmissions() {
  const [tab, setTab] = useState("posts"); // posts | comments
  const [status, setStatus] = useState("ALL"); // ALL | PENDING | APPROVED | REJECTED | ARCHIVED
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const initialType = (searchParams.get("type") === "PRAISE" || searchParams.get("type") === "PRAYER") ? searchParams.get("type") : "ALL";
  const [postType, setPostType] = useState(initialType); // ALL | PRAYER | PRAISE

  const isAuthed = useMemo(() => true, []);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      localStorage.getItem("theme") || "light"
    );
  }, []);

  useEffect(() => {
    let cancel = false;
    const controller = new AbortController();

    async function fetchMine() {
      setLoading(true);
      setErr("");
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
        });
        if (status !== "ALL") params.set("status", status);
        if (tab === "posts" && postType !== "ALL") {
          params.set("type", postType);
        }
        if (q.trim()) {
          params.set("q", q.trim());
        }
        const url = tab === "posts" ? "/api/community/me/posts" : "/api/community/me/comments";
        const res = await axios.get(`${url}?${params.toString()}`, {
          withCredentials: true,
          signal: controller.signal,
        });
        if (!cancel) {
          const data = res?.data || {};
          setItems(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (!cancel) setErr("Cannot load submissions");
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    if (isAuthed) fetchMine();
    return () => {
      cancel = true;
      controller.abort();
    };
  }, [tab, status, page, postType, q, isAuthed]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">My Conversations</h1>
          <p className="text-slate-600 text-sm sm:text-base">View and manage your conversations and comments</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 sm:mt-6">
        <div className="flex rounded-lg border overflow-hidden w-fit">
          <button
            onClick={() => { setPage(1); setTab("posts"); }}
            className={`px-3 sm:px-4 py-2 text-sm sm:text-base font-medium transition-colors duration-200 ${
              tab === "posts" 
                ? "bg-[#0074B9] text-white" 
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Posts
          </button>
          <button
            onClick={() => { setPage(1); setTab("comments"); }}
            className={`px-3 sm:px-4 py-2 text-sm sm:text-base font-medium transition-colors duration-200 ${
              tab === "comments" 
                ? "bg-[#0074B9] text-white" 
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Comments
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 sm:mt-6 flex flex-col gap-3 sm:gap-4">
        {/* Search */}
        <div className="relative w-full">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder={`Search ${tab === 'posts' ? 'conversations' : 'comments'}…`}
              className="w-full h-[42px] rounded-lg border border-gray-200 bg-white pl-10 pr-4 text-sm font-medium text-gray-700 shadow-sm placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all duration-200"
            />
          </div>
        </div>

        {/* Filter Row */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {tab === "posts" && (
            <CustomSelect
              value={postType}
              onChange={(value) => {
                setPage(1);
                setPostType(value);
                setSearchParams({ type: value });
              }}
              options={[
                { value: "ALL", label: "All Types" },
                { value: "PRAYER", label: "Prayer Requests" },
                { value: "PRAISE", label: "Praise Reports" }
              ]}
              placeholder="Post Type"
              className="sm:w-[140px]"
            />
          )}
          <CustomSelect
            value={status}
            onChange={(value) => {
              setPage(1);
              setStatus(value);
            }}
            options={[
              { value: "ALL", label: "All Status" },
              { value: "PENDING", label: "Pending" },
              { value: "APPROVED", label: "Approved" },
              { value: "REJECTED", label: "Rejected" },
              { value: "ARCHIVED", label: "Archived" }
            ]}
            placeholder="Status"
            className="sm:w-[140px]"
          />
        </div>
      </div>

      {/* Content */}
      <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
        {loading && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg sm:rounded-2xl border p-3 sm:p-5">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-slate-200" />
                  <div className="h-3 sm:h-4 w-32 sm:w-40 bg-slate-200 rounded" />
                </div>
                <div className="mt-3 h-12 sm:h-16 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        )}

        {!loading && err && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {err}
          </div>
        )}

        {!loading && !err && items.length === 0 && (
          <div className="rounded-lg sm:rounded-2xl border bg-white p-6 sm:p-8 text-center">
            <div className="text-xl sm:text-2xl mb-1">📝</div>
            <p className="text-slate-700 text-sm sm:text-base">
              {q.trim() ? `No ${tab === 'posts' ? 'conversations' : 'comments'} found matching "${q}"` : `No ${tab === 'posts' ? 'conversations' : 'comments'} yet`}
            </p>
          </div>
        )}

        {!loading && !err && items.map((it) => (
          <div key={it._id || it.id} className="rounded-lg border bg-white p-3 sm:p-5 shadow-sm">
            {tab === "posts" ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      it.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      it.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                      it.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      it.status === 'ARCHIVED' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {it.status}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(it.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="font-semibold text-lg mb-2">
                  {it.status === 'APPROVED' ? (
                    <a 
                      href={`${it.type === 'PRAISE' ? '/praise' : '/prayers'}/${it._id}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {it.title}
                    </a>
                  ) : (
                    it.title
                  )}
                </div>
                <div 
                  className="text-slate-800 line-clamp-3"
                  dangerouslySetInnerHTML={{ __html: it.content }}
                />
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      it.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      it.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                      it.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {it.status}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(it.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div 
                  className="text-slate-800"
                  dangerouslySetInnerHTML={{ __html: it.content }}
                />
                {it.postId && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        {it.postId.authorId?.avatar || it.postId.authorId?.profilePicture ? (
                          <img
                            className="w-8 h-8 rounded-full object-cover"
                            src={it.postId.authorId.avatar || it.postId.authorId.profilePicture}
                            alt={it.postId.authorId.name || 'User'}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                            (it.postId.authorId?.avatar || it.postId.authorId?.profilePicture) ? 'hidden' : 'flex'
                          }`}
                          style={{ backgroundColor: '#0074B9' }}
                        >
                          {(it.postId.authorId?.name || it.postId.authorId?.username || 'U').charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-gray-900 truncate">
                              {it.postId.status === 'APPROVED' ? (
                                <a 
                                  href={`${it.postId.type === 'PRAISE' ? '/praise' : '/prayers'}/${it.postId._id}`}
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {it.postId.title}
                                </a>
                              ) : (
                                it.postId.title
                              )}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              it.postId.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                              it.postId.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              it.postId.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {it.postId.status}
                            </span>
                          </div>
                          {it.postId.status === 'APPROVED' && (
                            <a
                              href={`${it.postId.type === 'PRAISE' ? '/praise' : '/prayers'}/${it.postId._id}`}
                              className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors duration-200 flex-shrink-0"
                            >
                              View Post
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{it.postId.type === 'PRAYER' ? 'Prayer Request' : 'Praise Report'}</span>
                          {it.postId.authorId?.name && (
                            <>
                              <span>•</span>
                              <span>by {it.postId.authorId.name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!loading && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button className="rounded-lg border px-3 py-1.5 disabled:opacity-50" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Prev</button>
          <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
          <button className="rounded-lg border px-3 py-1.5 disabled:opacity-50" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
        </div>
      )}
    </div>
  );
}


